import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../../generic-components/Icon';
import CloseIcon from '../../../../../assets/svg/x-icon-v2.svg';
import ReminderIcon from '../../../../../assets/svg/bell-icon.svg';
import Heading from '../../../../../generic-components/Heading';
import ToggleButton from '../../../InvoiceOnboarding/components/ToggleButton';
import { IInvoiceReminder } from '../../types/types';
import { ReminderItem } from './ReminderItem';
import Button from '../../../../../generic-components/Button';
import { TOGGLE_TYPES, STYLES, TEXTS } from '../../consts/consts';

const { INVOICE_CREATION: { INVOICE_REMINDERS_CONTROLLER: componentStyles } } = STYLES;

const { INVOICE_CREATION: { INVOICE_REMINDERS: componentTexts } } = TEXTS;

const EXAMPLE_INVOICE_URL = 'https://assets.lili.co/unified/invoicing/invoice_reminders_example_24_3_24@3x.png';

interface IProps {
    remindersList: IInvoiceReminder[];
    masterToggleStatus: boolean;
    onSaveToggles: (toggleList: IInvoiceReminder[], masterStatus: boolean) => void;
    dueDate: Date;
    handleClose: () => void;
}

export const RemindersController = ({
  remindersList, masterToggleStatus, dueDate, onSaveToggles, handleClose,
}: IProps) => {
  const [masterToggleSelected, setMasterToggleSelected] = useState<boolean>(masterToggleStatus);
  const [localReminders, setLocalReminders] = useState<IInvoiceReminder[]>([]);
  const { t } = useTranslation();

  const handleToggle = (type?: string) => {
    if (type === TOGGLE_TYPES.MASTER) {
      setMasterToggleSelected(prevState => !prevState);

      const updatedReminders = [...localReminders]
        .map((r) => {
          r.selected = !masterToggleSelected;
          return r;
        });
      setLocalReminders(updatedReminders);
      onSaveToggles(updatedReminders, masterToggleSelected);

      return;
    }
    const toggleIdx = localReminders.findIndex((reminder: IInvoiceReminder) => reminder.invoiceReminderType === type);
    if (toggleIdx !== -1) {
      const updatedReminders = [...localReminders];
      updatedReminders[toggleIdx].selected = !updatedReminders[toggleIdx].selected;
      setLocalReminders(updatedReminders);
      onSaveToggles(updatedReminders, masterToggleSelected);
    }
  };

  const handleSaveToggles = () => {
    onSaveToggles(localReminders, masterToggleSelected);
  };

  const activeRemindersCount = useMemo(() => {
    let counter = 0;
    if (!localReminders || !localReminders.length) return counter;
    localReminders.forEach((reminder:IInvoiceReminder) => {
      if (reminder.selected) {
        counter++;
      }
    });
    return counter;
  }, [localReminders]);

  useEffect(() => {
    if (!remindersList) return;
    setLocalReminders([...remindersList]);
  }, [remindersList]);

  useEffect(() => {
    if (activeRemindersCount && !masterToggleSelected) {
      setMasterToggleSelected(true);
    } else if (!activeRemindersCount && masterToggleSelected) {
      setMasterToggleSelected(false);
    }
  }, [activeRemindersCount]);

  return (
    <div className={componentStyles.WRAPPER}>
      <div className={componentStyles.HEADER_CONTAINER}>
        <Icon onClick={handleClose} src={CloseIcon} />
        <Heading title={t(componentTexts.MASTER_TOGGLE_LABEL)} className={componentStyles.HEADER} />
        <div className={componentStyles.PLACE_HOLDER_DIV} />
      </div>

      <div className={componentStyles.HERO_WRAPPER}>
        <Heading
          title={t(componentTexts.REMINDERS_DISCLAIMER)}
          className={componentStyles.HERO_HEADER}
        />

        <div className={componentStyles.MASTER_TOGGLE_WRAPPER}>
          <div className={componentStyles.MASTER_TOGGLE_LABEL_WRAPPER}>
            <Icon src={ReminderIcon} />
            <Heading title={t(componentTexts.MASTER_TOGGLE_LABEL)} className={componentStyles.MASTER_TOGGLE_LABEL} />
          </div>
          <ToggleButton toggleState={masterToggleSelected} toggle={() => handleToggle(TOGGLE_TYPES.MASTER)} buttonId='elButtonRemindersMasterToggle' />
        </div>

      </div>
      <div className={componentStyles.TOGGLE_LIST_CONTAINER}>
        {localReminders.map((reminder: IInvoiceReminder) => <ReminderItem dueDate={dueDate} handleToggle={handleToggle} reminderData={reminder} />) }
      </div>
      <div className={componentStyles.EXAMPLE_INVOICE_WRAPPER}>
        <Heading
          title={t('INVOICE_EXAMPLE_DISCLAIMER')}
          className={componentStyles.INVOICE_EXAMPLE_STYLES}
        />
        <img className='w-[44%]' alt='Example' src={EXAMPLE_INVOICE_URL} />
      </div>
      {false && (
      <div className={componentStyles.FLOATING_FOOTER_WRAPPER}>
        <Button onClick={handleClose} className={componentStyles.FOOTER_ACTION_CANCEL_BUTTON} id='elButtonCancelToggles' childElement={<Heading title={t(componentTexts.ACTION_CANCEL)} className={componentStyles.FOOTER_ACTION_CANCEL_BUTTON_CHILDREN} />} />
        <Button onClick={handleSaveToggles} className={componentStyles.FOOTER_ACTION_SAVE_BUTTON} id='elButtonSaveToggles' childElement={<Heading title={t(componentTexts.ACTION_SAVE)} className={componentStyles.FOOTER_ACTION_SAVE_BUTTON_CHILDREN} />} />
      </div>
      )}
    </div>
  );
};
